import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from 'react-apollo';

import usePendingDigitalBundles from '../../hooks/usePendingDigitalBundles';
import {
  getAlbumMembershipGroupEntity,
  isMultiIdentityBundleMembership,
} from '../../../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import { isAlbumFolder } from '../../../../../../../../../util/album-folder';
import Spinner from '../../../../../../../../../components/global/Spinner';
import PurchasedDigitalBundleOption from './components/PurchasedDigitalBundleOption';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import useOpenAlert from '../../../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../../../util/normalize-errors';
import { DIGITAL_BUNDLE_CONFIGURATION_COMPLETE_MODAL } from '../DigitalBundleConfigurationCompleteModal';

const propTypes = {
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
  onClose: PropTypes.func,
  successModalProps: PropTypes.object,
};

const DigitalBundlesMissingConfiguration = ({
  identity,
  albumMembershipGroup,
  isAllPhotos,
  onClose,
  successModalProps = {},
}) => {
  const [saving, setSaving] = useState(false);
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const client = useApolloClient();
  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);

  const {
    purchasedDigitalBundles,
    loadingPurchasedDigitalBundles,
    onPurchasedDigitalBundlePhotosCreate,
  } = usePendingDigitalBundles({
    identityId: identity?.id,
    albumFolderId: isAlbumFolder(entity)
      ? entity?.id
      : entity?.root_album_folder?.id,
    pendingConfigurationOnly:
      isAllPhotos || isMultiIdentityBundleMembership(albumMembershipGroup),
  });

  if (purchasedDigitalBundles.length === 0 || loadingPurchasedDigitalBundles) {
    return (
      <div className="w-100 text-center my-3">
        <Spinner />
      </div>
    );
  }

  return (
    <Fragment>
      <h4 className="text-center">Configure Digital Bundles below:</h4>
      <div className="digital-bundles-missing-configuration">
        {purchasedDigitalBundles.map(purchasedDigitalBundle => (
          <PurchasedDigitalBundleOption
            key={purchasedDigitalBundle.id}
            disabled={saving}
            isAllPhotos={isAllPhotos}
            purchasedDigitalBundle={purchasedDigitalBundle}
            albumMembershipGroup={albumMembershipGroup}
            onConfigurationComplete={async ({ photoIds }) => {
              if (saving) {
                return;
              }

              try {
                setSaving(true);

                const {
                  data: {
                    purchasedDigitalBundlePhotosCreate: { success },
                  },
                } = await onPurchasedDigitalBundlePhotosCreate({
                  purchasedDigitalBundleId: purchasedDigitalBundle.id,
                  photoIds,
                });

                if (success) {
                  await client.resetStore();
                  openModal(DIGITAL_BUNDLE_CONFIGURATION_COMPLETE_MODAL, {
                    photosAdded: photoIds.length,
                    ...successModalProps,
                  });
                  onClose();
                }
              } catch (e) {
                openAlert({
                  type: 'error',
                  info: getErrorString(e),
                  message: 'There was an error accepting this request.',
                });
              } finally {
                setSaving(false);
              }
            }}
          />
        ))}
      </div>
    </Fragment>
  );
};

DigitalBundlesMissingConfiguration.propTypes = propTypes;

export default DigitalBundlesMissingConfiguration;
